import dayjs from "@punchup/utils/dayjs";

/**
 * Reads a JWT's payload WITHOUT verifying it, for client-side decisions only (when to
 * refresh, which UI to offer, what an internal harness shows); the backend verifies every
 * token itself. Returns null for anything that is not a JWT with a JSON-object payload. No
 * jwt-decode dependency; the payload is UTF-8 decoded so a non-ASCII claim reads correctly.
 * The one JWT payload decoder: `apps/web` imports this, it does not keep a copy.
 */
export function decodeJwtPayload(
  token: string
): Record<string, unknown> | null {
  const segment = token.split(".")[1];
  if (!segment) return null;

  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
    const json = decodeURIComponent(
      Array.from(
        binary,
        (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`
      ).join("")
    );
    const payload: unknown = JSON.parse(json);
    return typeof payload === "object" &&
      payload !== null &&
      !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** The token's `exp` in seconds since the epoch, or null when it has none. */
export function getTokenExp(token: string | null | undefined): number | null {
  const exp = token ? decodeJwtPayload(token)?.exp : undefined;
  return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
}

/**
 * True when a token should be replaced before it is used: missing, unreadable, or within
 * `thresholdSeconds` of its `exp` (or past it). A readable token without an `exp` never
 * expires, the answer tixologist's isTokenExpiringSoon gives too.
 */
export function isTokenExpiringSoon(
  token: string | null | undefined,
  thresholdSeconds = 300
): boolean {
  if (!token || !decodeJwtPayload(token)) return true;

  const exp = getTokenExp(token);
  return exp !== null && exp - dayjs().unix() < thresholdSeconds;
}

/**
 * True when the token's member roles include `admin`. A UX affordance (offering the
 * admin-only scanner invite, say), not a security boundary: the backend enforces the
 * same. Partner logins carry the roles in the Firebase custom claims, `claims.memberRoles`.
 */
export function isAdminRole(token: string): boolean {
  const claims = token ? decodeJwtPayload(token)?.claims : undefined;
  if (typeof claims !== "object" || claims === null) return false;

  const roles = (claims as { memberRoles?: unknown }).memberRoles;
  return (
    typeof roles === "object" &&
    roles !== null &&
    Object.keys(roles).includes("admin")
  );
}
